import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@monksflow/monks-ui';
import { InfoIcon } from 'lucide-react';

import { AnalysisSelect, AnalysisSubHeader } from '../../../../components';
import type { MediaMetric } from '../../model/aggregate';
import type { DimensionKind } from '../../model/dimensions';
import type { MixCell, MixMode, MixTableView } from '../../model/mixTable';

interface MixTableProps {
  view: MixTableView;
  columns: string[];
  metric: MediaMetric;
  onMetricChange: (metric: MediaMetric) => void;
  kind: DimensionKind;
  onKindChange: (kind: DimensionKind) => void;
  mode: MixMode;
  onModeChange: (mode: MixMode) => void;
  selectedCount: number;
  totalCount: number;
}

/**
 * Warm-neutral heat wash. NOT `--primary`: that resolves to near-black in the default theme and
 * to purple in another, so a spend gradient drawn in it reads as brand colour rather than
 * intensity. The alpha ramp keeps the design's 0.05 floor, so a small but non-zero share still
 * tints rather than vanishing into an empty cell.
 */
const heatAlpha = (intensity: number) => 0.05 + Math.min(intensity, 1) * 0.6;

/** Above this wash the cell is dark enough that foreground ink loses contrast. */
const HEAT_INVERT_ALPHA = 0.42;

const heatStyle = (intensity: number) =>
  intensity <= 0
    ? undefined
    : {
        backgroundColor: `color-mix(in srgb, var(--color-neutral-600) ${Math.round(
          heatAlpha(intensity) * 100,
        )}%, transparent)`,
      };

const cellClassName = (cell: MixCell) =>
  clsx(
    'whitespace-nowrap text-right',
    cell.intensity > 0 && heatAlpha(cell.intensity) > HEAT_INVERT_ALPHA && 'text-neutral-50',
  );

export const MixTable = ({
  view,
  columns,
  metric,
  onMetricChange,
  kind,
  onKindChange,
  mode,
  onModeChange,
  selectedCount,
  totalCount,
}: MixTableProps) => {
  const { t } = useTranslation('analysis');
  const isCpm = metric === 'cpm';

  const metricOptions: Array<{ value: MediaMetric; label: string }> = [
    { value: 'spend', label: t('analysisResult.mediaLandscape.totalSpend') },
    { value: 'impressions', label: t('analysisResult.mediaLandscape.impressions') },
    { value: 'cpm', label: t('analysisResult.mediaLandscape.avgCpm') },
  ];
  const kindOptions: Array<{ value: DimensionKind; label: string }> = [
    { value: 'channels', label: t('analysisResult.mediaLandscape.channels') },
    { value: 'publishers', label: t('analysisResult.mediaLandscape.publishers') },
  ];

  const note = isCpm
    ? t('analysisResult.mediaLandscape.cpmNote')
    : mode === 'share'
      ? t('analysisResult.mediaLandscape.shareNote', {
          selected: selectedCount,
          total: totalCount,
        })
      : t('analysisResult.mediaLandscape.mixNote');

  const toggleClassName = (active: boolean) =>
    clsx(
      'px-3.5 py-1.5 text-sm transition-colors duration-fast ease-out disabled:cursor-not-allowed disabled:opacity-50',
      active ? 'bg-accent font-medium text-primary' : 'bg-card text-foreground-alt',
    );

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-start gap-3">
        <div className="flex min-w-0 flex-1 flex-col gap-2">
          <AnalysisSubHeader
            title={
              kind === 'publishers'
                ? t('analysisResult.mediaLandscape.publishersMixStrategy')
                : t('analysisResult.mediaLandscape.channelsMixStrategy')
            }
            subtitle={
              kind === 'publishers'
                ? t('analysisResult.mediaLandscape.publishersMixSubtitle')
                : t('analysisResult.mediaLandscape.channelsMixSubtitle')
            }
            subtitleTone="meta"
          />
          <span className="flex items-center gap-1.5 self-start rounded-md bg-accent px-2.5 py-1 text-xs text-accent-foreground">
            <InfoIcon className="size-3.5" />
            {note}
          </span>
        </div>
        <div className="flex overflow-hidden rounded-lg border border-border">
          <button
            type="button"
            disabled={isCpm}
            aria-pressed={mode === 'brand' && !isCpm}
            className={toggleClassName(mode === 'brand' && !isCpm)}
            onClick={() => onModeChange('brand')}
          >
            {t('analysisResult.mediaLandscape.brandMix')}
          </button>
          <button
            type="button"
            disabled={isCpm}
            aria-pressed={mode === 'share' && !isCpm}
            className={toggleClassName(mode === 'share' && !isCpm)}
            onClick={() => onModeChange('share')}
          >
            {t('analysisResult.mediaLandscape.marketShare')}
          </button>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2.5">
        <AnalysisSelect
          variant="filter"
          ariaLabel={t('analysisResult.mediaLandscape.metric')}
          value={metric}
          onValueChange={(next) => onMetricChange(next as MediaMetric)}
          options={metricOptions}
        />
        <AnalysisSelect
          variant="filter"
          ariaLabel={t('analysisResult.mediaLandscape.analyzeBy')}
          value={kind}
          onValueChange={(next) => onKindChange(next as DimensionKind)}
          options={kindOptions}
        />
      </div>
      <div className="overflow-x-auto rounded-xl border border-border-light bg-card p-4">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('analysisResult.mediaLandscape.brandName')}</TableHead>
              <TableHead className="text-right">
                {metricOptions.find((option) => option.value === metric)?.label}
              </TableHead>
              {columns.map((dimension) => (
                <TableHead key={dimension} className="text-right">
                  {isCpm
                    ? dimension
                    : t('analysisResult.mediaLandscape.dimensionColumn', { dimension })}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {view.rows.map((row) => (
              <TableRow key={row.advertiser}>
                <TableCell className="whitespace-nowrap font-medium">{row.advertiser}</TableCell>
                <TableCell className="whitespace-nowrap text-right font-medium">
                  {row.total}
                </TableCell>
                {row.cells.map((cell, i) => (
                  <TableCell
                    key={columns[i]}
                    className={cellClassName(cell)}
                    style={heatStyle(cell.intensity)}
                  >
                    <span className="block font-medium">{cell.primary}</span>
                    {cell.secondary && (
                      <span className="text-[11px] opacity-75">{cell.secondary}</span>
                    )}
                  </TableCell>
                ))}
              </TableRow>
            ))}
            <TableRow className="bg-surface">
              <TableCell className="font-bold">
                {t('analysisResult.mediaLandscape.totalMarket')}
              </TableCell>
              <TableCell className="whitespace-nowrap text-right font-bold">
                {view.marketTotal}
              </TableCell>
              {view.marketCells.map((cell, i) => (
                <TableCell key={columns[i]} className="text-right">
                  <span className="block font-bold">{cell.primary}</span>
                  {cell.secondary && (
                    <span className="text-[11px] text-muted-foreground">{cell.secondary}</span>
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableBody>
        </Table>
      </div>
    </div>
  );
};
